/**
 * Broccoli Score — Social Desirability Bias Classifier
 *
 * Named after the "broccoli response": participants claim to prefer healthier/more
 * socially acceptable answers when asked about behavior or decisions in surveys.
 *
 * References:
 *   - Nederhof (1985) — "Methods of coping with social desirability bias"
 *   - Paulhus (1991) — Balanced Inventory of Desirable Responding (BIDR)
 *   - Fisher (1993) — "Social desirability bias and the validity of indirect questioning"
 */

export type BroccoliClassification = 'low_social_desirability' | 'medium_social_desirability' | 'high_social_desirability'

export interface ReasonClassification {
  reason: string
  broccoli_score: number
  classification: BroccoliClassification
  rationale: string
  hidden_driver_hint: string
}

export interface BroccoliResult {
  overall_score: number
  per_reason: ReasonClassification[]
  narrative: string
}

// Canonical pattern library — ordered by matching priority
const PATTERNS: Array<{
  patterns: string[]
  score: number
  classification: BroccoliClassification
  rationale: string
  hidden_driver: string
}> = [
  {
    patterns: ['too expensive', 'price', 'cost', 'budget', 'afford', 'pricing', 'overpriced'],
    score: 0.85,
    classification: 'high_social_desirability',
    rationale: '"Too expensive" is the single most socially safe churn reason — it implies the product was good, just misaligned with budget. Paulhus (1991) found cost attributions have the highest face-validity deflection rate.',
    hidden_driver: 'Often masks: disengagement, perceived lack of value, or switching to a free/cheaper alternative that\'s "good enough."',
  },
  {
    patterns: ['missing feature', 'feature', 'functionality', 'capability', 'doesn\'t support', 'lack of', 'needs'],
    score: 0.60,
    classification: 'medium_social_desirability',
    rationale: 'Feature gaps are partially honest — they name a concrete issue — but are often selected because they shift blame to the vendor rather than user behavior.',
    hidden_driver: 'Often masks: the feature existed but wasn\'t discovered, or the user never invested in onboarding to use it.',
  },
  {
    patterns: ['switching', 'alternative', 'competitor', 'moved to', 'using another', 'found a better'],
    score: 0.55,
    classification: 'medium_social_desirability',
    rationale: 'Competitive displacement is plausible and moderately honest, but rarely includes the actual reason the alternative was sought.',
    hidden_driver: 'Often masks: the real trigger (a bad support experience, a key champion leaving, or accumulated friction) that prompted the search.',
  },
  {
    patterns: ['no longer need', 'don\'t need', 'not needed', 'use case gone', 'project ended', 'team restructure', 'company direction'],
    score: 0.25,
    classification: 'low_social_desirability',
    rationale: 'Situational changes are the most honest category — they admit a specific real-world event caused the churn, not a vendor failure or user preference.',
    hidden_driver: 'This is likely what it says. Confirm by asking: "Would you re-subscribe if the situation changed?" Yes = genuine situational churn.',
  },
  {
    patterns: ['forgot', 'didn\'t use', 'not using it', 'never logged in', 'no time', 'abandoned'],
    score: 0.10,
    classification: 'low_social_desirability',
    rationale: 'Admitting non-use is rare in surveys — it implicates the respondent. When it appears, it\'s almost always genuine.',
    hidden_driver: 'Root cause is almost always an onboarding or activation failure, not a product deficiency. High value to investigate.',
  },
  {
    patterns: ['poor support', 'bad support', 'customer service', 'not helpful', 'slow response', 'support issue'],
    score: 0.35,
    classification: 'low_social_desirability',
    rationale: 'Support complaints are moderately honest — they name a specific incident — but often represent the visible tip of accumulated dissatisfaction.',
    hidden_driver: 'The support issue is usually the last straw, not the root cause. Ask: "What were you trying to do when you contacted support?"',
  },
  {
    patterns: ['difficult', 'complicated', 'too complex', 'hard to use', 'confusing', 'steep learning curve', 'ux'],
    score: 0.40,
    classification: 'medium_social_desirability',
    rationale: 'Usability complaints are honest but vague — they name the symptom, not the specific friction point.',
    hidden_driver: 'Often masks: the user\'s specific workflow never matched the product\'s design assumptions. Ask: "Which specific action was hardest?"',
  },
]

function parseReasons(text: string): string[] {
  const lines = text.split(/\n|;/).map(l => l.trim()).filter(l => l.length > 0)
  return lines.map(l => {
    // Strip "Reason: 35%" or "35%: reason" formats
    return l.replace(/^\d+[%.)\-\s]+/, '').replace(/:\s*\d+%?$/, '').trim()
  }).filter(l => l.length > 2)
}

function classifyReason(reason: string): Omit<ReasonClassification, 'reason'> {
  const lower = reason.toLowerCase()
  for (const pattern of PATTERNS) {
    if (pattern.patterns.some(p => lower.includes(p))) {
      return {
        broccoli_score: pattern.score,
        classification: pattern.classification,
        rationale: pattern.rationale,
        hidden_driver_hint: pattern.hidden_driver,
      }
    }
  }
  // Default for unrecognized reasons
  return {
    broccoli_score: 0.5,
    classification: 'medium_social_desirability',
    rationale: 'Reason did not match a known broccoli pattern. Score is neutral.',
    hidden_driver_hint: 'Probe further: ask "Can you tell us more about what led to this decision?"',
  }
}

function generateNarrative(perReason: ReasonClassification[], overallScore: number): string {
  const highCount = perReason.filter(r => r.classification === 'high_social_desirability').length
  const lowCount = perReason.filter(r => r.classification === 'low_social_desirability').length
  const total = perReason.length || 1

  if (overallScore > 0.70) {
    return `${Math.round((highCount / total) * 100)}% of the stated reasons are highly socially desirable — meaning they're the "safe" answers respondents choose when they don't want to say something uncomfortable. This survey result likely understates genuine dissatisfaction, product-fit issues, and activation failures. Recommend running a follow-up with behavioral evidence to surface what's actually driving churn.`
  }
  if (overallScore > 0.45) {
    return `This survey shows a mixed picture: some respondents gave honest situational answers while others chose safer attribution frames. The honest signals are worth amplifying — those responders are telling you something real. Prioritize follow-up on the low-broccoli reasons, as those represent your most actionable churn intelligence.`
  }
  return `${Math.round((lowCount / total) * 100)}% of reasons score as low social desirability — meaning respondents are being unusually candid. This is high-quality churn data. The stated reasons are likely genuine and worth acting on directly. Consider whether your survey design created psychological safety that other teams should replicate.`
}

export function classifyChurnReasons(reasonsText: string): BroccoliResult {
  const parsed = parseReasons(reasonsText)
  if (parsed.length === 0) {
    return {
      overall_score: 0.5,
      per_reason: [],
      narrative: 'No valid churn reasons detected. Please paste one reason per line.',
    }
  }

  const perReason: ReasonClassification[] = parsed.map(reason => ({
    reason,
    ...classifyReason(reason),
  }))

  const overallScore = perReason.reduce((sum, r) => sum + r.broccoli_score, 0) / perReason.length

  return {
    overall_score: Math.round(overallScore * 100) / 100,
    per_reason: perReason,
    narrative: generateNarrative(perReason, overallScore),
  }
}

export function formatBroccoliScore(score: number): string {
  const pct = Math.round(score * 100)
  if (pct >= 80) return `${pct}/100 — Highly socially desirable. Your survey is probably being lied to.`
  if (pct >= 60) return `${pct}/100 — Moderately socially desirable. Mix of honest and safe answers.`
  if (pct >= 40) return `${pct}/100 — Moderately honest. Some useful signals present.`
  return `${pct}/100 — Unusually candid. This is high-quality churn data.`
}
